import toolsClient from "@/api/toolsClient"
import axios from "axios"

const API_ENDPOINTS = {
  MEDIA: "/api/media/",
  UPLOAD: "/upload/",
  TASK: "/task/",
}

/**
 * One media-library session per station: its token and the two clients built for it.
 *
 * These used to be module globals that every call to getLibrary() overwrote, so opening a second
 * station's AutoDJ page silently redirected uploads still running for the first. Each upload now
 * names its station, and resolves the session for that station rather than whichever loaded last.
 * Tokens stay in memory — a shared localStorage key cannot hold one per station.
 */
const sessions = new Map()

const uploadUrl = (token) => `${API_ENDPOINTS.UPLOAD}?token=${token}&folder=default`

const fetchToken = async (stationKey) => {
  const response = await toolsClient.get(`stations/${stationKey}/library_token/`)

  return response.data
}

const createClient = (session, baseURL) => {
  const client = axios.create({ baseURL })

  client.interceptors.request.use((config) => {
    config.headers["X-Auth-Token"] = session.token

    return config
  })

  client.interceptors.response.use(
    (response) => response,
    async (e) => {
      const request = e.config

      if (e.response?.status !== 401 || request._retry) {
        return Promise.reject(e)
      }

      request._retry = true

      const { token } = await fetchToken(session.stationKey)

      session.token = token
      request.headers["X-Auth-Token"] = token

      if (request.url.startsWith(API_ENDPOINTS.UPLOAD)) {
        request.url = uploadUrl(token)
      }

      return client(request)
    }
  )

  return client
}

const createSession = async (stationKey) => {
  const { token, url, uploader } = await fetchToken(stationKey)
  const session = { stationKey, token }

  session.libraryClient = createClient(session, url)
  session.uploaderClient = createClient(session, uploader)

  return session
}

const sessionFor = (stationKey) => {
  if (!sessions.has(stationKey)) {
    sessions.set(
      stationKey,
      createSession(stationKey).catch((e) => {
        sessions.delete(stationKey)

        throw e
      })
    )
  }

  return sessions.get(stationKey)
}

export const getLibrary = async (stationKey) => {
  try {
    await sessionFor(stationKey)
  } catch (e) {
    console.error("Error getting media library:", e)
    throw e
  }
}

export const searchMedia = async (stationKey, { search = "", offset = 0, limit = 10 }) => {
  try {
    const { libraryClient } = await sessionFor(stationKey)
    const params = new URLSearchParams({ offset, limit, ...(search && { search }) })
    const endpoint = search ? `${API_ENDPOINTS.MEDIA}search` : API_ENDPOINTS.MEDIA
    const response = await libraryClient.get(`${endpoint}?${params}`)
    return response.data
  } catch (e) {
    console.error("Error searching media:", e)
    throw e
  }
}

export const uploadMediaFile = async (stationKey, file, config) => {
  try {
    const session = await sessionFor(stationKey)
    const data = new FormData()
    data.append("name", file.name)
    data.append("file", file)
    const response = await session.uploaderClient.post(uploadUrl(session.token), data, config)
    return response.data
  } catch (e) {
    console.error("Error uploading media file:", e)
    throw e
  }
}

export const getMediaUploadStatus = async (stationKey, task) => {
  try {
    const { uploaderClient } = await sessionFor(stationKey)
    const response = await uploaderClient.get(`${API_ENDPOINTS.TASK}${task}`)
    return response.data
  } catch (e) {
    console.error("Error getting media upload status:", e)
    throw e
  }
}

export const updateMedia = async (stationKey, media) => {
  try {
    const { libraryClient } = await sessionFor(stationKey)
    const response = await libraryClient.put(`${API_ENDPOINTS.MEDIA}${media.key}`, {
      title: media.metadata.title,
      artist: media.metadata.artist,
      album: media.metadata.album,
    })
    return response.data
  } catch (e) {
    console.error("Error updating media file:", e)
    throw e
  }
}
